import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faInfoCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

const StatusDistributionChart = ({ data, onItemClick }) => {
  const [statusData, setStatusData] = useState([]);
  const [totalReferences, setTotalReferences] = useState(0);

  useEffect(() => {
    if (!data || data.length === 0) {
      setStatusData([]);
      setTotalReferences(0);
      return;
    }

    // Count occurrences of each status with proper safety checks
    const statusCounts = data.reduce((acc, item) => {
      // Safety check for item and header
      if (!item || !item.header) {
        console.warn(
          "StatusDistributionChart: Found item without header",
          item
        );
        return acc;
      }

      // Handle case where trafficStatus might be undefined
      const status = item.header.trafficStatus || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Calculate total valid items for percentage calculation
    const validItemsCount = data.filter((item) => item && item.header).length;

    // Convert to array and sort by count (descending)
    const statusArray = Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
      percentage:
        validItemsCount > 0 ? Math.round((count / validItemsCount) * 100) : 0,
    }));

    statusArray.sort((a, b) => b.count - a.count);

    setStatusData(statusArray);
    setTotalReferences(validItemsCount);
  }, [data]);

  // Function to get color class based on status
  const getStatusColorClass = (status) => {
    if (!status) return "bg-gray-200";

    try {
      switch (status.toLowerCase()) {
        case "alert":
          return "bg-yellow-500";
        case "in progress":
          return "bg-blue-500";
        case "arribo":
          return "bg-indigo-500";
        case "revalidacion":
          return "bg-purple-500";
        case "pagado":
          return "bg-green-500";
        case "delivered":
          return "bg-emerald-500";
        case "n/a":
        case "unknown":
          return "bg-gray-400";
        default:
          return "bg-gray-500";
      }
    } catch (error) {
      console.warn("Invalid status value:", status);
      return "bg-gray-200";
    }
  };

  // Get text color class based on status
  const getStatusTextColorClass = (status) => {
    if (!status) return "text-gray-700";

    try {
      switch (status.toLowerCase()) {
        case "alert":
          return "text-yellow-700";
        case "in progress":
          return "text-blue-700";
        case "arribo":
          return "text-indigo-700";
        case "revalidacion":
          return "text-purple-700";
        case "pagado":
          return "text-green-700";
        case "delivered":
          return "text-emerald-700";
        case "n/a":
        case "unknown":
          return "text-gray-600";
        default:
          return "text-gray-700";
      }
    } catch (error) {
      console.warn("Invalid status value:", status);
      return "text-gray-700";
    }
  };

  if (statusData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <FontAwesomeIcon icon={faChartBar} className="text-4xl mb-2" />
        <p>No status data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartBar} className="mr-2 text-blue-600" />
          References by Status
        </h3>
        <div className="text-xs text-gray-500 flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          Click on a status to filter
        </div>
      </div>

      <div className="flex-grow overflow-auto">
        <div className="space-y-3">
          {statusData.map((item) => (
            <div key={item.status} className="group">
              <div className="flex justify-between items-center mb-1">
                <div
                  className={`font-medium ${getStatusTextColorClass(
                    item.status
                  )} flex items-center cursor-pointer hover:opacity-80 transition-opacity`}
                  onClick={() => onItemClick && onItemClick(item.status)}
                >
                  {item.status}
                  {item.status && item.status.toLowerCase() === "alert" && (
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="ml-1 text-yellow-500"
                    />
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {item.count} ({item.percentage}%)
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 group-hover:h-4 transition-all duration-200">
                <div
                  className={`${getStatusColorClass(
                    item.status
                  )} h-full rounded-full group-hover:opacity-80 transition-opacity`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">Total References:</span>
          <span className="font-semibold text-gray-800">{totalReferences}</span>
        </div>
        {onItemClick && (
          <button
            className="w-full mt-3 bg-blue-50 text-blue-700 hover:bg-blue-100 py-2 rounded-md text-xs font-medium transition-colors"
            onClick={() => onItemClick(statusData[0]?.status || "")}
          >
            View All By Status
          </button>
        )}
      </div>
    </div>
  );
};

export default StatusDistributionChart;
